import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Truck,
  ScrollText,
  Car,
  Briefcase,
  Settings,
  ChevronLeft,
  ChevronRight,
  Menu,
  ShieldCheck,
  DollarSign,
  BarChart3,
  Newspaper,
  Wrench,
  MapPin,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  icon: any;
  label: string;
  end?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: 'Overview',
    items: [
      { to: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
    ]
  },
  {
    title: 'User Management',
    items: [
      { to: '/admin/users', icon: Users, label: 'All Users' },
      { to: '/admin/users/approval', icon: ShieldCheck, label: 'Approvals' },
    ]
  },
  {
    title: 'Logistics',
    items: [
      { to: '/admin/jobs', icon: Briefcase, label: 'Jobs' },
      { to: '/admin/vehicles', icon: Car, label: 'Vehicles' },
      { to: '/admin/verifications', icon: Truck, label: 'Verifications' },
    ]
  },
  {
    title: 'Content',
    items: [
      { to: '/admin/blog', icon: Newspaper, label: 'Blog' },
      { to: '/admin/feedback', icon: MessageSquare, label: 'Feedback' },
    ]
  },
  {
    title: 'Emergency Services',
    items: [
      { to: '/admin/recovery-companies', icon: MapPin, label: 'Recovery Companies' },
      { to: '/admin/mechanic-garages', icon: Wrench, label: 'Mechanic Garages' },
    ]
  },
  {
    title: 'System',
    items: [
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
      // { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/admin/settings', icon: Settings, label: 'Settings' },
    ]
  }
];

const AdminLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleSidebar = () => setIsCollapsed(!isCollapsed);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b bg-card">
        <span className="font-bold text-lg">Admin Panel</span>
        <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
          <Menu size={24} />
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "bg-card border-r border-border transition-all duration-300 ease-in-out flex flex-col fixed md:sticky top-0 h-screen z-40",
          isCollapsed ? "w-20" : "w-64",
          isMobileMenuOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0"
        )}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-border">
          {!isCollapsed && <span className="font-bold text-xl text-primary">Admin</span>}
          <Button
            variant="ghost"
            size="icon"
            className="hidden md:flex ml-auto"
            onClick={toggleSidebar}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Button>
          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden ml-auto"
            onClick={toggleMobileMenu}
          >
            <ChevronLeft size={18} />
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-6 px-2">
            {navGroups.map((group, groupIndex) => (
              <div key={group.title}>
                {!isCollapsed && (
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-2">
                    {group.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) => cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm font-medium",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        isCollapsed && "justify-center px-2"
                      )}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <item.icon size={20} />
                      {!isCollapsed && <span>{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
                {groupIndex < navGroups.length - 1 && !isCollapsed && (
                  <Separator className="my-4 mx-2 w-auto" />
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border">
          <div className={cn("flex items-center gap-3", isCollapsed && "justify-center")}>
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
              A
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden">
                <p className="text-sm font-medium truncate">Admin User</p>
                <p className="text-xs text-muted-foreground truncate">admin@example.com</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-x-hidden bg-muted/10 p-4 md:p-8">
        <div className="max-w-7xl mx-auto animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;